import React, { Fragment, useContext, useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Divider,
  InputNumber,
  Switch,
  Select,
  Tooltip,
  Row,
  Col,
  Space,
  Typography,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { Timestamp } from 'firebase/firestore';
import { StoreContext } from '../store/index';
import { addLeague } from './api';
import Field from '../fields/field';
import SearchLocationInput from '../components/mapsAddress';

function AddLeagueModal({ field, visible, onClose }) {
  const store = useContext(StoreContext);
  const {
    user: [user],
    places: [places],
  } = store;

  const { formatMessage } = useIntl();
  const { Option } = Select;
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [address, setAddress] = useState(undefined);
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState(8);

  useEffect(() => {
    form.setFieldsValue({
      rules: '',
      description: '',
      maxAllowedPlayers: 16,
      totalPlayers: 16,
      price: 100,
    });
  }, []);

  const setMapsAddress = value => {
    setAddress(value);
    form.setFieldsValue({
      address: value.address,
    });
  };

  const onFinish = async values => {
    const {
      name,
      type,
      maxAllowedPlayers,
      price,
      paymentMaxDate,
      startDate,
      description,
      totalPlayers,
      rules,
    } = values;
    try {
      const res = await addLeague(
        {
          name,
          address: address ? address.address : '',
          placeName: address ? address.name : '',
          totalPlayers,
          maxAllowedPlayers,
          price,
          startDate,
          paymentMaxDate,
          description: field ? field.description : description,
          rules,
          isPublic,
          isClosed: false,
          isFinished: false,
          organizerEmail: user.email,
          organizerName: user.nickname,
          userId: user.uid,
          logs: [
            {
              date: Timestamp.fromDate(new Date()),
              message: 'Liga creada',
              type: 'event',
            },
          ],
          teams: [],
          stats: [],
          type,
        },
        setLoading,
      );
      if (res) {
        onClose();
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  const onFinishFailed = values => {
    console.log(values);
  };

  return (
    <Modal
      title={formatMessage({ id: 'leagues.actions.add' })}
      open={visible}
      onOk={form.submit}
      okButtonProps={{ loading: loading }}
      onCancel={onClose}>
      {field && (
        <Fragment key="field">
          <Field field={field} />
          <Divider />
        </Fragment>
      )}
      <Form
        form={form}
        name="basic"
        layout="vertical"
        labelCol={{
          span: 6,
        }}
        wrapperCol={{
          span: 16,
        }}
        initialValues={{
          remember: false,
          size: 'small',
        }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off">
        <Form.Item label="" name="">
          <Space.Compact>
            <Row>
              <Space>
                {formatMessage({ id: 'shifts.add.modal.field.public' })}
                <Switch
                  className="switch"
                  defaultChecked
                  value={isPublic}
                  size="small"
                  onChange={() => setIsPublic(!isPublic)}
                />
                <Tooltip
                  title={formatMessage({
                    id: 'shifts.add.modal.field.public.tooltip',
                  })}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            </Row>
          </Space.Compact>
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'leagues.add.modal.field.name',
          })}
          name="name"
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'leagues.add.modal.field.name.tooltip',
              }),
            },
          ]}>
          <Input
            placeholder={formatMessage({
              id: 'leagues.add.modal.field.name',
            })}
          />
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'leagues.add.modal.field.type',
          })}
          name="type"
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'leagues.add.modal.field.type.tooltip',
              }),
            },
          ]}>
          <Select
            style={{ width: '100%' }}
            placeholder={formatMessage({
              id: 'leagues.add.modal.field.type.tooltip',
            })}
            value={places}
            allowClear
            onClear={() => setIsPlaceSelected(false)}>
            {[
              { name: 'Liga' },
              { name: 'Liga (con playoff)' },
              { name: 'Copa' },
            ].map(({ name }) => (
              <Option key={name}>{`${name}`}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={formatMessage({ id: 'shifts.add.modal.field.players' })}
          name="totalPlayers"
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'shifts.add.modal.field.players' }),
            },
          ]}
          shouldUpdate={(prevValues, curValues) => prevValues !== curValues}>
          <Row align="middle">
            <Col>
              <InputNumber
                min={2}
                max={30}
                defaultValue={16}
                step={2}
                formatter={value =>
                  value % 2 === 0 ? value : Number(value) + 1
                }
                onChange={value => {
                  form.setFieldsValue({
                    totalPlayers: value,
                  });
                  setTeamPlayers(value / 2);
                }}
              />
            </Col>
            <Col offset={1}>
              <Typography>{`${teamPlayers} VS ${teamPlayers}`}</Typography>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'leagues.add.modal.field.players.max.allowed',
          })}
          name="maxAllowedPlayers"
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'leagues.add.modal.field.players.max.allowed.tooltip',
              }),
            },
          ]}
          shouldUpdate={(prevValues, curValues) => prevValues !== curValues}>
          <Row align="middle">
            <InputNumber min={teamPlayers} max={99} defaultValue={16} />
          </Row>
        </Form.Item>
        <Form.Item
          label={formatMessage({ id: 'shifts.add.modal.field.price' })}
          name="price"
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'shifts.add.modal.field.price.tooltip',
              }),
            },
          ]}>
          <Space.Compact>
            <Row>
              <Space>
                <InputNumber
                  min={0}
                  max={500}
                  defaultValue={100}
                  onChange={value =>
                    form.setFieldsValue({
                      price: value,
                    })
                  }
                />
                <Tooltip
                  title={formatMessage({
                    id: 'shifts.add.modal.field.price.tooltip',
                  })}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            </Row>
          </Space.Compact>
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'leagues.add.modal.field.paymentMaxDate',
          })}
          name="paymentMaxDate"
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'leagues.add.modal.field.paymentMaxDate.tooltip',
              }),
            },
          ]}>
          <Space.Compact>
            <DatePicker
              showTime={{ format: 'HH' }}
              disabledDate={current => current < new Date(new Date() + 1)}
              onOk={value =>
                form.setFieldsValue({
                  paymentMaxDate: Timestamp.fromDate(value.toDate()),
                })
              }
            />
          </Space.Compact>
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'leagues.add.modal.field.startDate',
          })}
          name="startDate"
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'leagues.add.modal.field.startDate.tooltip',
              }),
            },
          ]}>
          <Space.Compact>
            <DatePicker
              showTime={{ format: 'HH' }}
              disabledDate={current => current < new Date(new Date() + 1)}
              onOk={value =>
                form.setFieldsValue({
                  startDate: Timestamp.fromDate(value.toDate()),
                })
              }
            />
          </Space.Compact>
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'leagues.add.modal.field.rules',
          })}
          name="rules"
          rules={[
            {
              required: false,
              message: formatMessage({
                id: 'leagues.add.modal.field.rules.tooltip',
              }),
            },
          ]}>
          <Input.TextArea
            placeholder={formatMessage({
              id: 'leagues.add.modal.field.rules',
            })}
            autoSize={{
              minRows: 2,
              maxRows: 6,
            }}
          />
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'shifts.add.modal.field.description',
          })}
          name="description"
          rules={[
            {
              required: false,
              message: formatMessage({
                id: 'shifts.add.modal.field.description.tooltip',
              }),
            },
          ]}>
          <Input.TextArea
            placeholder={formatMessage({
              id: 'shifts.add.modal.field.description',
            })}
            autoSize={{
              minRows: 2,
              maxRows: 6,
            }}
          />
        </Form.Item>
        <Form.Item
          label={formatMessage({ id: 'shifts.add.modal.field.place' })}
          name="place"
          rules={[
            {
              required: false,
              message: 'Seleccione el lugar',
            },
          ]}>
          <Select
            style={{ width: '100%' }}
            placeholder={formatMessage({ id: 'shifts.field.help' })}
            value={places}
            allowClear
            onClear={() => setIsPlaceSelected(false)}
            onChange={value => {
              if (value) {
                setIsPlaceSelected(true);
              }
              setMapsAddress(places.find(p => p.id === value));
            }}>
            {places.map(({ name, id }) => (
              <Option key={id}>{`${name}`}</Option>
            ))}
          </Select>
        </Form.Item>
        {!isPlaceSelected && (
          <Form.Item
            label={formatMessage({ id: 'shifts.add.modal.field.address' })}
            name="address"
            rules={[
              {
                required: false,
                message: formatMessage({
                  id: 'shifts.add.modal.field.address.tooltip',
                }),
              },
            ]}>
            <SearchLocationInput setMapsAddress={setMapsAddress} />
          </Form.Item>
        )}
        {address?.address && (
          <Form.Item label="Direccion">{address?.address}</Form.Item>
        )}
      </Form>
    </Modal>
  );
}

export default AddLeagueModal;
